import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Key, Plus, Copy, Trash2, Search, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Save } from 'lucide-react';

const ApiKeys = () => {
    const { authFetch } = useAuth();
    const [apiKeys, setApiKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Create form state
    const [isCreating, setIsCreating] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', description: '', expires_at: '' });
    
    // Generated key modal
    const [generatedKey, setGeneratedKey] = useState(null);
    const [showKeyModal, setShowKeyModal] = useState(false);
    const [keyCopied, setKeyCopied] = useState(false);
    
    // Permissions management
    const [selectedKeyId, setSelectedKeyId] = useState(null);
    const [endpointCatalog, setEndpointCatalog] = useState(null);
    const [keyPermissions, setKeyPermissions] = useState({});
    const [expandedCategories, setExpandedCategories] = useState({});
    const [savingPermissions, setSavingPermissions] = useState(false);
    
    // Curl examples
    const [expandedCurls, setExpandedCurls] = useState({});
    const [copiedCurls, setCopiedCurls] = useState({});

    useEffect(() => {
        fetchApiKeys();
        fetchEndpointCatalog();
        // eslint-disable-next-line
    }, []);

    const fetchApiKeys = async () => {
        setLoading(true);
        try {
            const res = await authFetch('/api/api-keys/');
            if (res.ok) {
                const data = await res.json();
                setApiKeys(data);
            }
        } catch (error) {
            console.error("Error fetching API keys:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchEndpointCatalog = async () => {
        try {
            const res = await authFetch('/api/api-keys/endpoint-catalog');
            if (res.ok) {
                const data = await res.json();
                setEndpointCatalog(data);
            }
        } catch (error) {
            console.error("Error fetching endpoint catalog:", error);
        }
    };

    const fetchKeyPermissions = async (keyId) => {
        try {
            const res = await authFetch(`/api/api-keys/${keyId}/permissions`);
            if (res.ok) {
                const permissions = await res.json();
                // Convert to Set for easy lookup: "METHOD:pattern"
                const permSet = new Set(
                    permissions.map(p => `${p.http_method}:${p.endpoint_pattern}`)
                );
                setKeyPermissions(prev => ({ ...prev, [keyId]: permSet }));
            }
        } catch (error) {
            console.error("Error fetching permissions:", error);
        }
    };

    const handleCreate = async () => {
        if (!createForm.name.trim()) {
            alert("Name is required");
            return;
        }
        
        try {
            const payload = {
                name: createForm.name,
                description: createForm.description || null,
                expires_at: createForm.expires_at || null
            };
            
            const res = await authFetch('/api/api-keys/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            
            if (res.ok) {
                const data = await res.json();
                setGeneratedKey(data);
                setShowKeyModal(true);
                setIsCreating(false);
                setCreateForm({ name: '', description: '', expires_at: '' });
                fetchApiKeys();
            } else {
                const error = await res.json();
                alert(`Error: ${error.error || 'Failed to create API key'}`);
            }
        } catch (error) {
            console.error("Error creating API key:", error);
            alert("Error creating API key");
        }
    };

    const handleRevoke = async (keyId, keyName) => {
        if (!window.confirm(`Are you sure you want to revoke "${keyName}"? This action cannot be undone.`)) {
            return;
        }
        
        try {
            const res = await authFetch(`/api/api-keys/${keyId}`, {
                method: 'DELETE'
            });
            
            if (res.ok) {
                setApiKeys(apiKeys.filter(k => k.id !== keyId));
                if (selectedKeyId === keyId) {
                    setSelectedKeyId(null);
                }
            } else {
                alert("Error revoking API key");
            }
        } catch (error) {
            console.error("Error revoking API key:", error);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setKeyCopied(true);
        setTimeout(() => setKeyCopied(false), 2000);
    };

    const togglePermission = (keyId, method, pattern) => {
        const permKey = `${method}:${pattern}`;
        const currentPerms = keyPermissions[keyId] || new Set();
        const newPerms = new Set(currentPerms);
        
        if (newPerms.has(permKey)) {
            newPerms.delete(permKey);
        } else {
            newPerms.add(permKey);
        }
        
        setKeyPermissions(prev => ({ ...prev, [keyId]: newPerms }));
    };

    const savePermissions = async (keyId) => {
        setSavingPermissions(true);
        try {
            const currentPerms = keyPermissions[keyId] || new Set();
            
            // Fetch existing permissions from server
            const res = await authFetch(`/api/api-keys/${keyId}/permissions`);
            const existingPerms = res.ok ? await res.json() : [];
            const existingSet = new Set(
                existingPerms.map(p => `${p.http_method}:${p.endpoint_pattern}`)
            );
            
            // Calculate additions and removals
            const toAdd = [];
            const toRemove = [];
            
            currentPerms.forEach(perm => {
                if (!existingSet.has(perm)) {
                    const [method, pattern] = perm.split(':');
                    toAdd.push({ http_method: method, endpoint_pattern: pattern });
                }
            });
            
            existingSet.forEach(perm => {
                if (!currentPerms.has(perm)) {
                    const [method, pattern] = perm.split(':');
                    toRemove.push({ http_method: method, endpoint_pattern: pattern });
                }
            });
            
            // Execute additions
            if (toAdd.length > 0) {
                await authFetch(`/api/api-keys/${keyId}/permissions`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ permissions: toAdd })
                });
            }
            
            // Execute removals
            if (toRemove.length > 0) {
                await authFetch(`/api/api-keys/${keyId}/permissions`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ permissions: toRemove })
                });
            }
            
            alert("Permissions saved successfully");
        } catch (error) {
            console.error("Error saving permissions:", error);
            alert("Error saving permissions");
        } finally {
            setSavingPermissions(false);
        }
    };

    const selectKey = (keyId) => {
        setSelectedKeyId(keyId);
        if (!keyPermissions[keyId]) {
            fetchKeyPermissions(keyId);
        }
    };

    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const filteredKeys = apiKeys.filter(k =>
        k.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        k.key_prefix.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Never';
        return new Date(dateStr).toLocaleString();
    };

    const generateCurlExample = (method, pattern) => {
        const baseUrl = window.location.origin;
        let curl = `curl -X ${method}`;
        curl += ` \\\n  -H "Authorization: Bearer YOUR_API_KEY"`;
        
        if (['POST', 'PUT', 'PATCH'].includes(method)) {
            curl += ` \\\n  -H "Content-Type: application/json"`;
            
            // Generate example body based on endpoint pattern
            let exampleBody = {};
            
            if (pattern.includes('/send-text')) {
                exampleBody = { conversation_id: "conv-id", text: "Hello" };
            } else if (pattern.includes('/send-template')) {
                exampleBody = { conversation_id: "conv-id", template_id: "template-id", parameters: [] };
            } else if (pattern.includes('/start')) {
                exampleBody = { phone: "+34600000000" };
            } else if (pattern.includes('/close')) {
                exampleBody = { tramite_id: "tramite-id" };
            } else if (pattern.includes('/update')) {
                exampleBody = { data: {} };
            } else if (pattern.includes('/by-phones')) {
                exampleBody = { phones: ["+34600000000"] };
            } else if (pattern.includes('/link')) {
                exampleBody = { conversation_id: "conv-id", tramite_id: "tramite-id" };
            } else if (pattern.includes('/comparador')) {
                exampleBody = { cups: "ES0000000000000000AA", id_tarifa: "1" };
            } else if (pattern.includes('/extract')) {
                exampleBody = { file_url: "https://..." };
            } else if (pattern.includes('metadata')) {
                exampleBody = { metadata: {} };
            } else if (pattern.includes('/ingest')) {
                exampleBody = { conversation_id: "conv-id", text: "message" };
            } else if (pattern.includes('/fields') && method === 'POST') {
                exampleBody = { field_name: "field", label: "Label", examples: [] };
            } else if (pattern.includes('/en-gestion')) {
                exampleBody = { en_gestion: true };
            } else {
                exampleBody = { /* Consultar documentación del endpoint */ };
            }
            
            curl += ` \\\n  -d '${JSON.stringify(exampleBody, null, 2)}'`;
        }
        
        // Replace path parameters with examples
        let finalPattern = pattern
            .replace('{conversation_id}', 'conv-123')
            .replace('{tramite_id}', 'tramite-456')
            .replace('{phone}', '+34600000000')
            .replace('{contact_id}', 'contact-789')
            .replace('{agent_uuid}', 'agent-uuid')
            .replace('{attachment_id}', 'attach-101')
            .replace('{wa_message_id}', 'msg-202')
            .replace('{field_id}', 'field-303')
            .replace('{role_id}', 'role-404')
            .replace('{offer_id}', 'offer-505')
            .replace('{campaign_id}', 'campaign-606');
        
        curl += ` \\\n  "${baseUrl}${finalPattern}"`;
        
        return curl;
    };

    const toggleCurlExpanded = (endpointKey) => {
        setExpandedCurls(prev => ({
            ...prev,
            [endpointKey]: !prev[endpointKey]
        }));
    };

    const copyCurlToClipboard = (endpointKey, curlText) => {
        navigator.clipboard.writeText(curlText);
        setCopiedCurls(prev => ({ ...prev, [endpointKey]: true }));
        setTimeout(() => {
            setCopiedCurls(prev => ({ ...prev, [endpointKey]: false }));
        }, 2000);
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Key className="text-blue-400" size={32} />
                    <h1 className="text-2xl font-bold text-slate-50">API Keys</h1>
                </div>
                <button
                    onClick={() => setIsCreating(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                    <Plus size={18} />
                    Create API Key
                </button>
            </div>

            {/* Search */}
            <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                    type="text"
                    placeholder="Search by name or prefix..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500"
                />
            </div>

            {/* Create Form Modal */}
            {isCreating && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4">
                        <h2 className="text-xl font-bold text-slate-50 mb-4">Create API Key</h2>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    value={createForm.name}
                                    onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-slate-200"
                                    placeholder="Production API Key"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={createForm.description}
                                    onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-slate-200"
                                    placeholder="Used for production integration..."
                                    rows={3}
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">
                                    Expires At (optional)
                                </label>
                                <input
                                    type="datetime-local"
                                    value={createForm.expires_at}
                                    onChange={(e) => setCreateForm({...createForm, expires_at: e.target.value})}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-slate-200"
                                />
                            </div>
                        </div>
                        
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={handleCreate}
                                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                            >
                                Generate Key
                            </button>
                            <button
                                onClick={() => {
                                    setIsCreating(false);
                                    setCreateForm({ name: '', description: '', expires_at: '' });
                                }}
                                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Generated Key Modal */}
            {showKeyModal && generatedKey && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-slate-800 rounded-lg p-6 max-w-lg w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <AlertTriangle className="text-yellow-400" size={24} />
                            <h2 className="text-xl font-bold text-slate-50">Save Your API Key</h2>
                        </div>
                        
                        <p className="text-slate-300 mb-4">
                            This is the only time you'll see this key. Copy it now and store it securely.
                        </p>
                        
                        <div className="bg-slate-900 border border-slate-700 rounded p-4 mb-4">
                            <div className="flex items-center justify-between gap-2">
                                <code className="text-sm text-green-400 break-all flex-1">
                                    {generatedKey.plaintext_key}
                                </code>
                                <button
                                    onClick={() => copyToClipboard(generatedKey.plaintext_key)}
                                    className="p-2 bg-slate-700 hover:bg-slate-600 rounded transition-colors flex-shrink-0"
                                >
                                    {keyCopied ? (
                                        <CheckCircle2 className="text-green-400" size={18} />
                                    ) : (
                                        <Copy className="text-slate-300" size={18} />
                                    )}
                                </button>
                            </div>
                        </div>
                        
                        <div className="bg-yellow-900/20 border border-yellow-700/50 rounded p-3 mb-4">
                            <p className="text-yellow-200 text-sm">
                                ⚠️ Remember to configure permissions for this API key before using it.
                            </p>
                        </div>
                        
                        <button
                            onClick={() => {
                                setShowKeyModal(false);
                                setGeneratedKey(null);
                                setKeyCopied(false);
                            }}
                            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                        >
                            I've Saved the Key
                        </button>
                    </div>
                </div>
            )}

            {/* API Keys List */}
            {loading ? (
                <div className="text-center py-8 text-slate-400">Loading...</div>
            ) : filteredKeys.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                    {searchTerm ? 'No API keys match your search' : 'No API keys created yet'}
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredKeys.map((key) => (
                        <div key={key.id} className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                            {/* Key Header */}
                            <div className="p-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h3 className="text-lg font-semibold text-slate-50">{key.name}</h3>
                                            <span className={`px-2 py-1 text-xs rounded ${
                                                key.is_active ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                                            }`}>
                                                {key.is_active ? 'Active' : 'Revoked'}
                                            </span>
                                        </div>
                                        <div className="text-sm text-slate-400 space-y-1">
                                            <p><span className="font-mono">{key.key_prefix}...</span></p>
                                            {key.description && <p>{key.description}</p>}
                                            <p>Created: {formatDate(key.created_at)}</p>
                                            <p>Last used: {formatDate(key.last_used_at)}</p>
                                            {key.expires_at && <p>Expires: {formatDate(key.expires_at)}</p>}
                                        </div>
                                    </div>
                                    
                                    <div className="flex gap-2">
                                        {key.is_active && (
                                            <>
                                                <button
                                                    onClick={() => selectKey(key.id)}
                                                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                                                >
                                                    Manage Permissions
                                                </button>
                                                <button
                                                    onClick={() => handleRevoke(key.id, key.name)}
                                                    className="p-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Permissions Panel */}
                            {selectedKeyId === key.id && endpointCatalog && (
                                <div className="border-t border-slate-700 p-4 bg-slate-900/50">
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-lg font-semibold text-slate-50">Endpoint Permissions</h4>
                                        <button
                                            onClick={() => savePermissions(key.id)}
                                            disabled={savingPermissions}
                                            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white text-sm rounded transition-colors"
                                        >
                                            <Save size={16} />
                                            {savingPermissions ? 'Saving...' : 'Save Permissions'}
                                        </button>
                                    </div>

                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                        {Object.entries(endpointCatalog.endpoints_by_category).map(([category, endpoints]) => (
                                            <div key={category} className="border border-slate-700 rounded">
                                                <button
                                                    onClick={() => toggleCategory(category)}
                                                    className="w-full flex items-center justify-between p-3 bg-slate-800 hover:bg-slate-750 transition-colors"
                                                >
                                                    <span className="font-medium text-slate-200">
                                                        {endpointCatalog.categories[category]} ({endpoints.length})
                                                    </span>
                                                    {expandedCategories[category] ? (
                                                        <ChevronDown size={18} className="text-slate-400" />
                                                    ) : (
                                                        <ChevronRight size={18} className="text-slate-400" />
                                                    )}
                                                </button>

                                                {expandedCategories[category] && (
                                                    <div className="p-3 space-y-2">
                                                        {endpoints.map((endpoint) => (
                                                            <div
                                                                key={`${endpoint.method}:${endpoint.pattern}`}
                                                                className="border-b border-slate-700 last:border-0"
                                                            >
                                                                <label className="flex items-start gap-3 p-2 hover:bg-slate-800 rounded cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={(keyPermissions[key.id] || new Set()).has(
                                                                            `${endpoint.method}:${endpoint.pattern}`
                                                                        )}
                                                                        onChange={() => togglePermission(key.id, endpoint.method, endpoint.pattern)}
                                                                        className="mt-1"
                                                                    />
                                                                    <div className="flex-1">
                                                                        <div className="flex items-center gap-2 mb-1">
                                                                            <span className={`px-2 py-0.5 text-xs font-mono rounded ${
                                                                                endpoint.method === 'GET' ? 'bg-blue-900/30 text-blue-400' :
                                                                                endpoint.method === 'POST' ? 'bg-green-900/30 text-green-400' :
                                                                                endpoint.method === 'PUT' ? 'bg-yellow-900/30 text-yellow-400' :
                                                                                endpoint.method === 'PATCH' ? 'bg-purple-900/30 text-purple-400' :
                                                                                'bg-red-900/30 text-red-400'
                                                                            }`}>
                                                                                {endpoint.method}
                                                                            </span>
                                                                            <span className="font-mono text-sm text-slate-300">
                                                                                {endpoint.pattern}
                                                                            </span>
                                                                        </div>
                                                                        <p className="text-xs text-slate-400 mb-2">{endpoint.description}</p>
                                                                        
                                                                        {/* Curl Example */}
                                                                        <div className="mt-2">
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    toggleCurlExpanded(`${endpoint.method}:${endpoint.pattern}`);
                                                                                }}
                                                                                className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                                                                            >
                                                                                {expandedCurls[`${endpoint.method}:${endpoint.pattern}`] ? (
                                                                                    <ChevronDown size={12} />
                                                                                ) : (
                                                                                    <ChevronRight size={12} />
                                                                                )}
                                                                                Ejemplo curl
                                                                            </button>
                                                                            
                                                                            {expandedCurls[`${endpoint.method}:${endpoint.pattern}`] && (
                                                                                <div className="mt-2 relative">
                                                                                    <pre className="text-[10px] bg-slate-950 p-2 rounded border border-slate-700 text-slate-300 overflow-x-auto">
                                                                                        {generateCurlExample(endpoint.method, endpoint.pattern)}
                                                                                    </pre>
                                                                                    <button
                                                                                        onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            copyCurlToClipboard(
                                                                                                `${endpoint.method}:${endpoint.pattern}`,
                                                                                                generateCurlExample(endpoint.method, endpoint.pattern)
                                                                                            );
                                                                                        }}
                                                                                        className="absolute top-1 right-1 p-1 bg-slate-800 hover:bg-slate-700 rounded text-slate-400 hover:text-slate-200 transition-colors"
                                                                                        title="Copiar"
                                                                                    >
                                                                                        {copiedCurls[`${endpoint.method}:${endpoint.pattern}`] ? (
                                                                                            <CheckCircle2 size={12} className="text-green-400" />
                                                                                        ) : (
                                                                                            <Copy size={12} />
                                                                                        )}
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ApiKeys;
